import React, { useEffect, useState } from "react";
import { getUsers, getCurrentUser } from "../services/API";
import Layout from "./Layout";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Box,
    Typography,
    Button,
    IconButton,
    TextField,
    InputAdornment,
    Avatar,
} from "@mui/material";
import "../assets/css/usuarios.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { styled } from "@mui/material/styles";
import { Link, useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import GroupsIcon from "@mui/icons-material/Groups";
import { deleteUser } from "../services/API";

const StyledTableRow = styled(TableRow)(() => ({
    "&:nth-of-type(odd)": {
        backgroundColor: "rgba(248, 250, 252, 0.8)",
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
    "&:hover": {
        backgroundColor: "rgba(249, 115, 22, 0.04)",
        transition: "background-color 0.15s ease",
    },
    transition: "background-color 0.15s ease",
}));

const StyledTableCell = styled(TableCell)(() => ({
    backgroundColor: "#0f172a",
    color: "#f1f5f9",
    fontWeight: 600,
    fontSize: "0.78rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    padding: "14px 16px",
    borderBottom: "none",
    fontFamily: "inherit",
}));

const ROLES_MAP = {
    1: { name: "Super User",      bg: "#fef3c7", color: "#92400e", border: "#fcd34d" },
    2: { name: "Administrator",   bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" },
    3: { name: "Analyst",         bg: "#d1fae5", color: "#065f46", border: "#6ee7b7" },
    4: { name: "Guest",           bg: "#f3f4f6", color: "#374151", border: "#d1d5db" },
};

const USER_STATES_MAP = {
    1: { name: "Active",      bg: "#dcfce7", color: "#166534", border: "#86efac" },
    2: { name: "Suspended",   bg: "#fff7ed", color: "#9a3412", border: "#fdba74" },
    3: { name: "Unverified",  bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" },
};

const AVATAR_COLORS = [
    "#f97316","#3b82f6","#8b5cf6","#10b981","#f59e0b",
    "#ef4444","#06b6d4","#ec4899","#84cc16","#6366f1",
];

function getInitials(firstName = "", lastName = "") {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function getAvatarColor(id) {
    return AVATAR_COLORS[(id || 0) % AVATAR_COLORS.length];
}

function Usuarios() {
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUserRole, setCurrentUserRole] = useState(null);
    const [currentUserID, setCurrentUserID] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        const fetchUsersData = async () => {
            setLoading(true);
            setError(null);
            try {
                const currentUser = await getCurrentUser();
                setCurrentUserRole(currentUser.userRolesId);
                setCurrentUserID(currentUser.id);

                const response = await getUsers();

                if (Array.isArray(response)) {
                    let filteredUsers = [];

                    if (currentUser.userRolesId === 1) {
                        filteredUsers = response;
                    } else if (currentUser.userRolesId === 2) {
                        filteredUsers = response.filter(user =>
                            user.userRolesId === 2 || user.userRolesId === 3 || user.userRolesId === 4
                        );
                    } else if (currentUser.userRolesId === 3) {
                        filteredUsers = response.filter(user =>
                            user.userRolesId === 3 || user.userRolesId === 4
                        );
                    } else {
                        filteredUsers = [];
                    }

                    setUsuarios(filteredUsers);
                } else {
                    setUsuarios([]);
                    setError("Unexpected data format received from server.");
                }
            } catch (err) {
                console.error("Error loading users:", err);
                setError("Error loading users. Please try again later.");
                setUsuarios([]);
            } finally {
                setLoading(false);
            }
        };

        fetchUsersData();
    }, []);

    const handleEdit = (userId) => {
        navigate(`/actualizar-usuario/${userId}`);
    };

    const handleDelete = async (userId, firstName, lastName) => {
        if (window.confirm(`Are you sure you want to delete user ${firstName} ${lastName} (Not reversible)`)) {
            await deleteUser(userId);
            setUsuarios(usuarios.filter(usuario => usuario.id !== userId));
            alert(`User ${userId} deleted.`);
        }
    };

    const filteredUsuarios = usuarios.filter(u => {
        const term = searchTerm.toLowerCase();
        return (
            u.firstName?.toLowerCase().includes(term) ||
            u.lastName?.toLowerCase().includes(term) ||
            u.email?.toLowerCase().includes(term)
        );
    });

    return (
        <Layout>
            <div className="app-container">
                <Box sx={{ padding: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto", width: "100%" }}>

                    {/* ── Header ── */}
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2, mb: 3 }}>
                                <Typography variant="h4" sx={{
                                    fontFamily: "inherit",
                                    fontWeight: 700,
                                    fontSize: { xs: "1.6rem", md: "2rem" },
                                    color: "#0f172a",
                                    mb: 0.5
                                }}>
                                    Users List
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#94a3b8", fontFamily: "inherit" }}>
                                    {usuarios.length} registered {usuarios.length === 1 ? "user" : "users"}
                                </Typography>

                            {(currentUserRole === 1 || currentUserRole === 2 || currentUserRole === 3) && (
                                <Button
                                    variant="contained"
                                    component={Link}
                                    to="/registrar-usuario"
                                    startIcon={<PersonAddAltIcon />}
                                    sx={{
                                        backgroundColor: "var(--jc-orange)",
                                        color: "#fff",
                                        fontWeight: 600,
                                        borderRadius: "10px",
                                        textTransform: "none",
                                        px: 3,
                                        py: 1.2,
                                        fontSize: "0.9rem",
                                        boxShadow: "none",
                                        fontFamily: "inherit",
                                        "&:hover": {
                                            backgroundColor: "var(--jc-orange-dark)",
                                            boxShadow: "none",
                                            transform: "translateY(-1px)",
                                        },
                                        transition: "all 0.2s ease",
                                    }}
                                >
                                    Register User
                                </Button>
                            )}
                        </Box>

                        {/* Search bar */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by name or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "#94a3b8", fontSize: "1.1rem" }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                maxWidth: 420,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    backgroundColor: "#fff",
                                    fontFamily: "inherit",
                                    "& fieldset": { borderColor: "#e2e8f0" },
                                    "&:hover fieldset": { borderColor: "#cbd5e1" },
                                    "&.Mui-focused fieldset": { borderColor: "var(--jc-orange)" },
                                },
                            }}
                        />
                    </Box>

                    {/* ── Content ── */}
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 240, flexDirection: "column", gap: 2 }}>
                            <CircularProgress sx={{ color: "var(--jc-orange)" }} />
                            <Typography variant="body2" sx={{ color: "#94a3b8", fontFamily: "inherit" }}>
                                Loading users...
                            </Typography>
                        </Box>

                    ) : error ? (
                        <Box sx={{
                            display: "flex", justifyContent: "center", alignItems: "center",
                            height: 240, flexDirection: "column", gap: 1,
                            background: "#fff8f7", border: "1px solid #fee2e2",
                            borderRadius: "16px", p: 4,
                        }}>
                            <Typography variant="h6" sx={{ color: "#b91c1c", fontFamily: "inherit", fontWeight: 600 }}>
                                {error}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#94a3b8", fontFamily: "inherit" }}>
                                Check your connection or try again later.
                            </Typography>
                        </Box>

                    ) : filteredUsuarios.length > 0 ? (
                        <Paper elevation={0} sx={{
                            borderRadius: "16px",
                            overflow: "hidden",
                            border: "1px solid #e2e8f0",
                            boxShadow: "0 4px 24px rgba(15, 23, 42, 0.06)",
                        }}>
                            <TableContainer>
                                <Table sx={{ minWidth: 700 }} aria-label="users table">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell sx={{ width: 52 }}>#</StyledTableCell>
                                            <StyledTableCell>User</StyledTableCell>
                                            <StyledTableCell>Email</StyledTableCell>
                                            <StyledTableCell>Role</StyledTableCell>
                                            <StyledTableCell>Status</StyledTableCell>
                                            {(currentUserRole === 1 || currentUserRole === 2 || currentUserRole === 3) && (
                                                <StyledTableCell align="center">Actions</StyledTableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredUsuarios.map((usuario, index) => {
                                            const roleInfo  = ROLES_MAP[usuario.userRolesId]  || { name: "Unknown", bg: "#f1f5f9", color: "#64748b", border: "#cbd5e1" };
                                            const stateInfo = USER_STATES_MAP[usuario.userStatesId] || { name: "Unknown", bg: "#f1f5f9", color: "#64748b", border: "#cbd5e1" };

                                            return (
                                                <StyledTableRow key={usuario.id}>
                                                    {/* Index */}
                                                    <TableCell sx={{ color: "#94a3b8", fontWeight: 500, fontSize: "0.82rem", fontFamily: "inherit" }}>
                                                        {index + 1}
                                                    </TableCell>

                                                    {/* User avatar + name */}
                                                    <TableCell>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                            <Avatar sx={{
                                                                width: 36, height: 36,
                                                                backgroundColor: getAvatarColor(usuario.id),
                                                                fontSize: "0.78rem",
                                                                fontWeight: 700,
                                                                fontFamily: "inherit",
                                                                flexShrink: 0,
                                                            }}>
                                                                {getInitials(usuario.firstName, usuario.lastName)}
                                                            </Avatar>
                                                            <Typography sx={{
                                                                fontWeight: 600,
                                                                fontSize: "0.88rem",
                                                                color: "#1e293b",
                                                                fontFamily: "inherit",
                                                            }}>
                                                                {usuario.firstName} {usuario.lastName}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>

                                                    {/* Email */}
                                                    <TableCell>
                                                        <Typography sx={{ fontSize: "0.84rem", color: "#475569", fontFamily: "inherit" }}>
                                                            {usuario.email}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* Role badge */}
                                                    <TableCell>
                                                        <Box sx={{
                                                            display: "inline-flex", alignItems: "center",
                                                            px: 1.5, py: 0.35,
                                                            borderRadius: "20px",
                                                            backgroundColor: roleInfo.bg,
                                                            border: `1px solid ${roleInfo.border}`,
                                                        }}>
                                                            <Typography sx={{
                                                                fontSize: "0.75rem", fontWeight: 600,
                                                                color: roleInfo.color,
                                                                fontFamily: "inherit",
                                                                letterSpacing: "0.03em",
                                                                whiteSpace: "nowrap",
                                                            }}>
                                                                {roleInfo.name}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>

                                                    {/* Status badge */}
                                                    <TableCell>
                                                        <Box sx={{
                                                            display: "inline-flex", alignItems: "center", gap: 0.6,
                                                            px: 1.5, py: 0.35,
                                                            borderRadius: "20px",
                                                            backgroundColor: stateInfo.bg,
                                                            border: `1px solid ${stateInfo.border}`,
                                                        }}>
                                                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: stateInfo.color, flexShrink: 0 }} />
                                                            <Typography sx={{
                                                                fontSize: "0.75rem", fontWeight: 600,
                                                                color: stateInfo.color,
                                                                fontFamily: "inherit",
                                                                letterSpacing: "0.03em",
                                                            }}>
                                                                {stateInfo.name}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>

                                                    {/* Actions */}
                                                    {(currentUserRole === 1 || currentUserRole === 2 || currentUserRole === 3) && (
                                                        <TableCell align="center">
                                                            <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75 }}>
                                                                <IconButton
                                                                    aria-label="edit"
                                                                    size="small"
                                                                    onClick={() => handleEdit(usuario.id)}
                                                                    sx={{
                                                                        width: 32, height: 32,
                                                                        backgroundColor: "#eff6ff",
                                                                        color: "#3b82f6",
                                                                        borderRadius: "8px",
                                                                        "&:hover": { backgroundColor: "#dbeafe", transform: "scale(1.08)" },
                                                                        transition: "all 0.15s ease",
                                                                    }}
                                                                >
                                                                    <EditIcon sx={{ fontSize: "0.95rem" }} />
                                                                </IconButton>
                                                                <IconButton
                                                                    aria-label="delete"
                                                                    size="small"
                                                                    onClick={() => handleDelete(usuario.id, usuario.firstName, usuario.lastName)}
                                                                    disabled={usuario.id === currentUserID}
                                                                    sx={{
                                                                        width: 32, height: 32,
                                                                        backgroundColor: "#fff1f2",
                                                                        color: "#ef4444",
                                                                        borderRadius: "8px",
                                                                        "&:hover": { backgroundColor: "#fee2e2", transform: "scale(1.08)" },
                                                                        "&.Mui-disabled": { backgroundColor: "#f8fafc", color: "#cbd5e1" },
                                                                        transition: "all 0.15s ease",
                                                                    }}
                                                                >
                                                                    <DeleteIcon sx={{ fontSize: "0.95rem" }} />
                                                                </IconButton>
                                                            </Box>
                                                        </TableCell>
                                                    )}
                                                </StyledTableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Footer with search count */}
                            {searchTerm && (
                                <Box sx={{ px: 3, py: 1.5, borderTop: "1px solid #f1f5f9", backgroundColor: "#fafafa" }}>
                                    <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8", fontFamily: "inherit" }}>
                                        Showing {filteredUsuarios.length} of {usuarios.length} users
                                    </Typography>
                                </Box>
                            )}
                        </Paper>

                    ) : (
                        <Box sx={{
                            display: "flex", justifyContent: "center", alignItems: "center",
                            height: 240, flexDirection: "column", gap: 1.5,
                            border: "2px dashed #e2e8f0", borderRadius: "16px", p: 4,
                        }}>
                            <GroupsIcon sx={{ fontSize: "2.5rem", color: "#cbd5e1" }} />
                            <Typography variant="h6" sx={{ color: "#94a3b8", fontFamily: "inherit", fontWeight: 600 }}>
                                {searchTerm ? "No users found" : "No users registered"}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#cbd5e1", fontFamily: "inherit" }}>
                                {searchTerm ? `No matches for "${searchTerm}"` : "There are no user records to display yet."}
                            </Typography>
                        </Box>
                    )}

                </Box>
            </div>
        </Layout>
    );
}

export default Usuarios;

